// ─── Page-level access (Collaboration › a person › Role & Permissions) ────────
//
// The menu IS the permission tree: every main-menu section, every submenu
// heading and expandable group, and every page under them is a node (ACCESS_TREE
// in components/sidebar/Sidebar.js is built from the same data the sidebar
// draws, so a new page is permissioned the moment it is in the menu).
//
// A person's grants are a sparse map, node id → 'none' | 'read' | 'write'. A
// node with no entry takes its nearest ancestor's level, so granting a section
// covers every page under it and an entry on a page overrides just that page.
// Read opens the page; Write also lets them change things on it.
//
// Node ids are dotted slug paths: 'sales', 'sales.ai-agents',
// 'sales.ai-agents.place-online-order', 'security.package.production-tickets'.

export const LEVELS = ['none', 'read', 'write'];
export const LEVEL_LABELS = { none: 'No access', read: 'Read', write: 'Write' };

export const accessSlug = (label) => String(label || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const isLevel = (v) => v === 'none' || v === 'read' || v === 'write';
const parentOf = (id) => { const i = String(id).lastIndexOf('.'); return i > 0 ? id.slice(0, i) : ''; };

/** The level that applies to a node: its own entry, else its nearest ancestor's, else none. */
export function levelOf(access, id) {
    for (let cur = id; cur; cur = parentOf(cur)) {
        const v = access?.[cur];
        if (isLevel(v)) return v;
    }
    return 'none';
}

/** The node, or anything under it, can be opened: a section shows in the menu
 *  when any page inside it can. */
export function reachable(access, id) {
    if (levelOf(access, id) !== 'none') return true;
    const prefix = `${id}.`;
    return Object.entries(access || {}).some(([k, v]) => k.startsWith(prefix) && (v === 'read' || v === 'write'));
}

/** Set one node's level: its descendants drop their own entries and inherit
 *  it, and an entry that only repeats what it would inherit is left out. */
export function withLevel(access, id, level) {
    const prefix = `${id}.`;
    const next = Object.fromEntries(Object.entries(access || {}).filter(([k]) => k !== id && !k.startsWith(prefix)));
    const inherited = parentOf(id) ? levelOf(next, parentOf(id)) : 'none';
    if (inherited !== level) next[id] = level;
    return next;
}

// ─── Presets (the Role tiles) ────────────────────────────────────────────────
// Top-level sections, in menu order. Sidebar's ACCESS_TREE sections must match
// this list (access.test.js checks), so a preset never misses a section.
export const ACCESS_SECTIONS = [
    'dashboard', 'control', 'sales', 'labels', 'inventory', 'purchasing',
    'locations', 'staffs',
    'wholesale',
    'downloads', 'security',
    'collaboration', 'settings',
    'tools',
];

const every = (level) => Object.fromEntries(ACCESS_SECTIONS.map((s) => [s, level]));

export const PRESET_ACCESS = {
    admin: every('write'),
    // Runs the business day to day: everything except the team and settings.
    manager: { ...every('write'), collaboration: 'read', settings: 'read' },
    // Sees everything, changes nothing; no team or settings.
    viewer: { ...every('read'), collaboration: 'none', settings: 'none' },
    // Register + label printing on the floor.
    cashier: {
        dashboard: 'read', sales: 'read', labels: 'write', 'labels.setup': 'read',
        'inventory.stock.products': 'read', 'inventory.stock.brands': 'read',
        'locations.devices': 'read', downloads: 'read',
    },
    // Receiving and back of house.
    warehouse: {
        dashboard: 'read', locations: 'read', inventory: 'write', downloads: 'read',
        'purchasing.purchasing.purchase-orders': 'write', 'purchasing.purchasing.vendors': 'read',
        'purchasing.materials': 'write',
    },
    // The wholesale desk, with read on what they sell.
    wholesale: { dashboard: 'read', sales: 'read', inventory: 'read', wholesale: 'write', downloads: 'read' },
    // Books: read the money views.
    accountant: {
        dashboard: 'read', control: 'read', sales: 'read', wholesale: 'read',
        // Brands is the same catalog seen by brand: whoever reads Products reads it.
        'inventory.stock.products': 'read', 'inventory.stock.brands': 'read',
        'purchasing.purchasing.purchase-orders': 'read', 'purchasing.purchasing.vendors': 'read',
    },
};

export const presetAccess = (role) => ({ ...(PRESET_ACCESS[role] || {}) });

// ─── Workspace roles ─────────────────────────────────────────────────────────
// A workspace can add roles and edit the built-in ones (owner, 2026-10-13:
// "make it possible to create a new role and edit a role"); hooks/
// useWorkspaceRoles merges what it saved over the presets. Admin is never
// editable: an admin is unrestricted everywhere (context/AccessContext), and
// Custom only names access that matches no role.
export const LOCKED_ROLE_IDS = new Set(['admin', 'custom']);

/**
 * The first role whose access resolves the same on every page, or 'custom'.
 * `prefer` (the role the person was given) wins when two roles grant the same.
 */
export function detectRole(access, roles, nodeIds, prefer = '') {
    const ordered = prefer ? [...(roles || []).filter((r) => r?.id === prefer), ...(roles || []).filter((r) => r?.id !== prefer)] : roles;
    for (const r of ordered || []) {
        if (!r || r.id === 'custom') continue;
        if (nodeIds.every((id) => levelOf(access, id) === levelOf(r.access || {}, id))) return r.id;
    }
    return 'custom';
}

/** A new role's id from its name: readable, unique, never a locked id. */
export function roleIdFor(label, takenIds = []) {
    const taken = new Set([...takenIds, ...LOCKED_ROLE_IDS]);
    const base = accessSlug(label) || 'role';
    if (!taken.has(base)) return base;
    for (let n = 2; ; n += 1) if (!taken.has(`${base}-${n}`)) return `${base}-${n}`;
}

/** The preset whose resolved level matches on every node, or 'custom'. */
export function detectPreset(access, nodeIds) {
    for (const [role, preset] of Object.entries(PRESET_ACCESS)) {
        if (nodeIds.every((id) => levelOf(access, id) === levelOf(preset, id))) return role;
    }
    return 'custom';
}

// ─── Before page-level access ────────────────────────────────────────────────
// Records saved before Sep 2026 carry module grants ({ sales: { view, export } }).
// Each module lands on the part of the tree it covered: view alone is Read,
// any other action (create, edit, approve, print…) is Write.
const MODULE_NODES = {
    dashboard:       ['dashboard'],
    command_center:  ['control'],
    sales:           ['sales'],
    labels:          ['labels'],
    label_presets:   ['labels.setup'],
    locations:       ['locations'],
    devices:         ['locations.devices'],
    staffs:          ['staffs'],
    inventory:       ['inventory'],
    products:        ['inventory.stock.products'],
    purchase_orders: ['purchasing.purchasing.purchase-orders'],
    vendors:         ['purchasing.purchasing.vendors'],
    raw_materials:   ['purchasing.materials.raw-materials'],
    label_formats:   ['inventory.receiving.barcode-formats'],
    storage:         ['inventory.receiving.storage'],
    wholesale:       ['wholesale'],
    downloads:       ['downloads'],
    applications:    ['security'],
    collaboration:   ['collaboration'],
    settings:        ['settings'],
};
const LEGACY_MODULE_IDS = { invoices: 'wholesale' };

// Pages that moved in the menu. A saved access map still names the node the
// page had when the grant was written, so it is read under its new id instead
// of silently becoming "No access". Sep 2026: Purchase Orders, Vendors and Raw
// Materials moved into the new Purchasing section, which also took Conversion
// and Reordering from Production.
const MOVED_NODES = {
    'purchase-orders': 'purchasing.purchasing.purchase-orders',
    vendors: 'purchasing.purchasing.vendors',
    'raw-materials': 'purchasing.materials.raw-materials',
    'labels.materials.conversion': 'purchasing.materials.conversion',
    'labels.materials.quick-reorder': 'purchasing.materials.reordering',
    // Import Invoices moved under Purchasing's AI Agents heading hours after
    // it shipped; carry over any grant written in between.
    'purchasing.purchasing.import-invoices': 'purchasing.ai-agents.import-invoices',
    // 2026-09-29: the ticket stations moved from Package Devices to their own
    // Ticket Devices page, and Package Devices became the shipping stations.
    // The old id stays in the tree (it is still a page), so a grant on it now
    // covers both: the stations a person had keep opening, and the page they
    // had keeps its name.
    'locations.devices.package-devices': 'locations.devices.ticket-devices',
    // 2026-09-30: Command Center's "Cash" became "Daily Closeouts". Richard
    // Levin's grant (and anyone else's) was written under the old name.
    'control.money-in.cash': 'control.money-in.daily-closeouts',
    // 2026-10-10: the wholesale price pages moved under their own Pricing
    // heading ("Pricing" reads "Product Pricing" there). A grant on the old
    // Wholesale heading covered them, so it covers the new heading too.
    'wholesale.wholesale': 'wholesale.pricing',
    'wholesale.wholesale.pricing': 'wholesale.pricing.product-pricing',
    'wholesale.wholesale.price-levels': 'wholesale.pricing.price-levels',
    'wholesale.wholesale.price-adjustments': 'wholesale.pricing.price-adjustments',
    // 2026-10-20: Command Center's "Gift Certificates" became "Gift Cards"
    // (the route stays /control/gift-certificates). A grant written under
    // the old name keeps opening the page.
    'control.adjustments.gift-certificates': 'control.adjustments.gift-cards',
    // The same day, the wholesale email Settings moved from Applications ›
    // Wholesale to the Wholesale menu, right under Applications. The old group
    // held only that page, so a grant on either lands on it; the page's own
    // entry comes last and wins over the group's.
    'security.wholesale': 'wholesale.wholesale.settings',
    'security.wholesale.settings': 'wholesale.wholesale.settings',
    // 2026-10-10: Barcode Formats and Storage left the main menu's Receiving
    // group for the Inventory submenu's Receiving heading (owner: "move
    // inside the inventory menu"). A grant on either page keeps it; anyone
    // with Inventory access now reaches them at that same level.
    'barcode-formats': 'inventory.receiving.barcode-formats',
    storage: 'inventory.receiving.storage',
    // The same day Inventory's Reorder page went (owner: "a duplicate with
    // purchasing"); a grant on it, or on its Replenishment heading, now opens
    // Purchasing › Reordering, where /inventory/reorder redirects.
    'inventory.replenishment': 'purchasing.materials.reordering',
    'inventory.replenishment.reorder': 'purchasing.materials.reordering',
    // 2026-10-22: Locations › Delivery split into the Delivery Calendar (its
    // Shipping / Pickup / Local Delivery calendars) and Delivery Zip Codes. A
    // grant on the old page carries to both.
    'locations.locations.delivery': ['locations.locations.delivery-calendar', 'locations.locations.delivery-zip-codes'],
};

/** A saved access map with any moved page's grant also filed under its node's
 *  new id (the old entry stays); a page that became several carries its grant
 *  to each. An entry already written under the new id wins. */
export function withMovedNodes(access) {
    if (!access || typeof access !== 'object') return access;
    let out = null;
    for (const [from, to] of Object.entries(MOVED_NODES)) {
        if (access[from] === undefined) continue;
        for (const target of [].concat(to)) {
            if (access[target] !== undefined) continue;
            out = out || { ...access };
            out[target] = access[from];
        }
    }
    return out || access;
}

export function accessFromPermissions(perms) {
    if (!perms || typeof perms !== 'object' || Object.keys(perms).length === 0) return null;
    const access = {};
    Object.entries(MODULE_NODES).forEach(([mod, nodes]) => {
        const legacy = Object.keys(LEGACY_MODULE_IDS).find((k) => LEGACY_MODULE_IDS[k] === mod);
        const acts = perms[mod] || (legacy ? perms[legacy] : null) || {};
        const on = Object.entries(acts).filter(([, v]) => v).map(([a]) => a);
        const level = on.some((a) => a !== 'view') ? 'write' : on.includes('view') ? 'read' : 'none';
        nodes.forEach((n) => { access[n] = level; });
    });
    // The Wave assistant was open to everyone before it had its own node.
    access.tools = 'read';
    return access;
}

/** A collaborator record's grants: page-level `access`, else everything for
 *  an admin, else its module grants, else its role preset. */
export function accessOfRecord(record) {
    if (record?.access && typeof record.access === 'object') return withMovedNodes(record.access);
    if (String(record?.role || '').toLowerCase() === 'admin') return presetAccess('admin');
    return accessFromPermissions(record?.permissions) || presetAccess(record?.role || 'viewer');
}

/** can(key, action): key is a tree node id, or a module id from before
 *  page-level access. View asks whether it (or a page under it) opens; any
 *  other action asks for Write. */
export function canModule(access, key, action = 'view') {
    if (!key) return true;
    const nodes = MODULE_NODES[key] || [key];
    return nodes.some((n) => (action === 'view' ? reachable(access, n) : levelOf(access, n) === 'write'));
}

// ─── Routes → pages ──────────────────────────────────────────────────────────

/** Every node with its parent, depth and section, in menu order. */
export function flattenTree(tree) {
    const out = [];
    const walk = (nodes, parent, depth, section) => (nodes || []).forEach((n) => {
        const sectionId = section || n.id;
        out.push({ ...n, parentId: parent?.id || null, depth, sectionId });
        walk(n.children, n, depth + 1, sectionId);
    });
    walk(tree, null, 0, null);
    return out;
}

// Pages whose URL is not under their menu entry's URL.
const ROUTE_ALIASES = [
    [/^\/sales\/[^/]+$/, '/sales/database'],              // one order
    [/^\/labels\/[^/]+$/, '/labels/database'],            // one label
    [/^\/products(\/|$)/, '/inventory/products'],
    [/^\/inventory\/(quick-)?reorder$/, '/purchasing/reordering'],   // redirects there
    // Conversion and Reordering moved into Purchasing (Sep 2026); their old
    // URLs redirect, and answer to the new pages' nodes on the way.
    [/^\/labels\/conversion$/, '/purchasing/conversion'],
    [/^\/labels\/(quick-reorder|reordering)$/, '/purchasing/reordering'],
    [/^\/purchasing\/import$/, '/purchasing/agents/import-invoices'],
    // Daily Closeouts' old URL (it redirects) and the Daily Close alias.
    [/^\/control\/(cash|close)$/, '/control/closeouts'],
    // The Accounting page was removed (2026-10-10); its URL redirects.
    [/^\/control\/accounting$/, '/control/dashboard'],
    // The Batches page was removed (2026-10-10); its URLs redirect to Processor.
    [/^\/control\/(batches|deposits)$/, '/control/processor'],
    // One sent invoice or statement email (2026-10-10): it answers to the
    // wholesale orders it belongs with.
    [/^\/wholesale\/emails\/[^/]+$/, '/wholesale/orders'],
    // The wholesale email Settings' first URL (it redirects).
    [/^\/security\/wholesale\/settings$/, '/wholesale/settings'],
    [/^\/security\/events\//, '/security/dashboard'],     // one app event
    [/^\/orders$/, '/sales/database'],
    [/^\/users$/, '/collaboration'],
    [/^\/how-to-install$/, '/downloads'],
    [/^\/(billing|organizations|onboarding)(\/|$)/, '/settings'],
];

// Open to everyone signed in (what they show is already per person). Anyone
// in a workspace may ask the Wavelength team for help, so Support is too.
const ALWAYS_OPEN = [/^\/profile(\/|$)/, /^\/search(\/|$)/, /^\/support(\/|$)/];

export function routeIndex(tree) {
    const flat = flattenTree(tree);
    const pages = flat.filter((n) => n.href).sort((a, b) => b.href.length - a.href.length);
    return { flat, pages, byHref: new Map(pages.map((p) => [p.href, p])) };
}

/**
 * What a URL is, for the page guard:
 *   { kind: 'open' }             anyone signed in
 *   { kind: 'home' }             / or /dashboard (the Dashboard, else their first page)
 *   { kind: 'root', prefix }     a URL that only redirects into its pages
 *                                (/sales, /wholesale, /sales/email-audience)
 *   { kind: 'page', node }       a menu page, or a detail page under one
 *   { kind: 'unknown' }
 */
export function classifyPath(index, pathname) {
    const path = (String(pathname || '/').replace(/\/+$/, '') || '/');
    if (ALWAYS_OPEN.some((re) => re.test(path))) return { kind: 'open' };
    if (path === '/' || path === '/dashboard') return { kind: 'home' };
    const exact = index.byHref.get(path);
    if (exact) return { kind: 'page', node: exact };
    if (index.pages.some((p) => p.href.startsWith(`${path}/`))) return { kind: 'root', prefix: path };
    const under = index.pages.find((p) => path.startsWith(`${p.href}/`));
    if (under) return { kind: 'page', node: under };
    const alias = ROUTE_ALIASES.find(([re]) => re.test(path));
    const aliased = alias ? index.byHref.get(alias[1]) : null;
    if (aliased) return { kind: 'page', node: aliased };
    return { kind: 'unknown' };
}

/** The first page (menu order) this access can open: anywhere, inside one
 *  section, or under one URL prefix. */
export function firstOpenPage(index, access, { sectionId = null, prefix = null } = {}) {
    return index.flat.find((n) => n.href
        && (!sectionId || n.sectionId === sectionId)
        && (!prefix || n.href.startsWith(`${prefix}/`))
        && levelOf(access, n.id) !== 'none') || null;
}
